import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sun, Moon, Wrench, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Avatar } from "../components/Avatar";
import { useAuth } from "../lib/AuthContext";
import { useTheme } from "../lib/ThemeContext";
import { formatError } from "../lib/api";

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateMe, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user?.name || "");

  const saveName = async () => {
    const next = nameInput.trim();
    if (!next || next === user?.name) {
      setEditingName(false);
      setNameInput(user?.name || "");
      return;
    }
    try {
      await updateMe({ name: next });
      toast.success("Name updated");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setEditingName(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-8" data-testid="settings-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="settings-back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
          Settings
        </div>
        <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">
          App preferences
        </h1>
      </motion.div>

      {/* Account — only place the user can change their avatar + name.
          The Profile page intentionally renders this same data read-only
          and routes here when tapped. */}
      <div className="mt-6 tup-card p-5" data-testid="settings-account-card">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6] mb-3">
          Account
        </div>
        <div className="flex items-center gap-4">
          <Avatar
            user={user}
            size={64}
            editable
            onChanged={async () => { await refresh(); }}
            testId="settings-avatar"
          />
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  data-testid="settings-name-input"
                  className="bg-white border-[#E2E0D8] h-9"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName();
                    if (e.key === "Escape") {
                      setEditingName(false);
                      setNameInput(user?.name || "");
                    }
                  }}
                />
                <button
                  onClick={saveName}
                  data-testid="settings-save-name-btn"
                  className="text-[#277A82] p-2"
                  aria-label="Save name"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditingName(false); setNameInput(user?.name || ""); }}
                  data-testid="settings-cancel-name-btn"
                  className="text-[#8A93A6] p-2"
                  aria-label="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setEditingName(true); setNameInput(user?.name || ""); }}
                data-testid="settings-edit-name-btn"
                className="flex items-center gap-1.5 group"
              >
                <span className="font-heading text-xl font-semibold text-[#2D3142]">
                  {user?.name}
                </span>
                <Pencil className="w-3.5 h-3.5 text-[#8A93A6] group-hover:text-[#277A82]" />
              </button>
            )}
            <div className="text-sm text-[#8A93A6] truncate mt-0.5">{user?.email}</div>
          </div>
        </div>
      </div>

      <div
        className="mt-4 tup-card p-5"
        data-testid="settings-appearance-card"
      >
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
          Appearance
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            { key: "light", label: "Light", icon: Sun },
            { key: "dark",  label: "Dark",  icon: Moon },
          ].map((opt) => {
            const Icon = opt.icon;
            const active = theme === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  setTheme(opt.key);
                  toast.success(`${opt.label} mode on`);
                }}
                data-testid={`settings-mode-${opt.key}`}
                className={`rounded-2xl p-4 border-2 flex flex-col items-center justify-center gap-2 transition-colors ${
                  active
                    ? "border-[#277A82] bg-[#DCEDF0]/50 text-[#1F5566]"
                    : "border-[#E2E0D8] bg-white text-[#4F5D75] hover:border-[#277A82]/60"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-semibold">{opt.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#8A93A6] mt-3 leading-snug">
          Your choice is saved on this device. We respect your system preference on first visit.
        </p>
      </div>

      <div
        className="mt-4 tup-card p-5 flex items-start gap-3"
        data-testid="settings-more-coming-card"
      >
        <Wrench className="w-5 h-5 text-[#8A93A6] mt-0.5 shrink-0" />
        <div>
          <div className="font-heading text-base font-semibold text-[#2D3142]">
            More settings coming
          </div>
          <p className="text-xs text-[#4F5D75] mt-1 leading-relaxed">
            We're keeping this simple for now. Language, units, and accessibility
            controls will land here in an upcoming release.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
